import React, { useState, useEffect } from 'react';
import { Save, ShieldCheck, Calendar, Info, RefreshCw } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import api from '../../../utils/api';
import { toast } from 'sonner';

const ElectricalSettings: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [settings, setSettings] = useState({
        defaultElectricalCadenceNumber: 30,
        defaultElectricalCadenceUnit: 'days' as 'days' | 'weeks' | 'months'
    });

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            setLoading(true);
            const result = await api.get('/api/assets/settings/electrical');
            if (result) {
                setSettings({
                    defaultElectricalCadenceNumber: result.defaultElectricalCadenceNumber || 30,
                    defaultElectricalCadenceUnit: result.defaultElectricalCadenceUnit || 'days'
                });
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            await api.patch('/api/assets/settings/electrical', settings);
            toast.success('Electrical settings updated');
        } catch (err) {
            console.error(err);
            toast.error('Failed to update settings');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="max-w-3xl space-y-8 animate-in fade-in duration-500">
            <div className="bg-white rounded-[32px] border border-gray-100 shadow-sm overflow-hidden">
                <div className="p-8 space-y-8">
                    {/* General Cadence Setting */}
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 pb-8 border-b border-gray-50">
                        <div className="flex-1 space-y-1">
                            <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center text-blue-500">
                                    <Calendar size={16} />
                                </div>
                                <h4 className="font-bold text-gray-900">Default Inspection Cadence</h4>
                            </div>
                            <p className="text-xs text-gray-500">Number of days, weeks, or months before the next Test & Tag inspection is due by default.</p>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="w-24 relative group">
                                <Input
                                    type="number"
                                    value={settings.defaultElectricalCadenceNumber}
                                    onChange={(e) => setSettings({ ...settings, defaultElectricalCadenceNumber: parseInt(e.target.value) })}
                                    className="h-12 rounded-xl border-gray-100 bg-gray-50/50 font-bold"
                                />
                            </div>
                            <div className="w-32">
                                <select
                                    value={settings.defaultElectricalCadenceUnit}
                                    onChange={(e) => setSettings({ ...settings, defaultElectricalCadenceUnit: e.target.value as any })}
                                    className="w-full h-12 rounded-xl border border-gray-100 bg-gray-50/50 px-4 font-bold focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                                >
                                    <option value="days">Days</option>
                                    <option value="weeks">Weeks</option>
                                    <option value="months">Months</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Information Box */}
                    <div className="bg-slate-900 rounded-3xl p-6 text-white relative overflow-hidden group">
                        <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
                            <ShieldCheck size={120} />
                        </div>
                        <div className="relative flex gap-4">
                            <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
                                <Info size={20} className="text-blue-300" />
                            </div>
                            <div className="space-y-2">
                                <h5 className="font-bold text-sm">Regulatory Compliance</h5>
                                <p className="text-xs text-slate-400 leading-relaxed">
                                    The Australian Standard (AS/NZS 3760) provides guidelines for inspection intervals. While 30 days is common for some hostile environments, other settings may vary between 3 to 12 months. Ensure your default cadence aligns with your industry regulations.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="p-6 bg-gray-50/50 border-t border-gray-100 flex items-center justify-between">
                    <Button
                        variant="ghost"
                        onClick={fetchSettings}
                        disabled={loading || saving}
                        className="rounded-xl gap-2 text-gray-500 h-11"
                    >
                        <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
                        Reset
                    </Button>
                    <Button
                        onClick={handleSave}
                        disabled={saving}
                        className="bg-slate-900 hover:bg-slate-800 text-white rounded-xl px-12 h-11 font-bold gap-2 shadow-lg shadow-slate-200 transition-all"
                    >
                        <Save size={18} />
                        {saving ? 'Saving...' : 'Save Settings'}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default ElectricalSettings;
